import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaSearch } from "react-icons/fa";
import hkLocations from "../../i18n/hong_kong_locations.json";

function TutorCenterSearch({ onSearch, districts = [] }) {
  const { t, i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const comboRef = useRef(null);
  const [filterText, setFilterText] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const firstInputRef = useRef(null);
  const previousActiveElement = useRef(null);

  const localeKey = (i18n?.language || "en").startsWith("zh") ? "tw" : "en";

  const regionOptions = useMemo(() => {
    try {
      const base = (hkLocations.regions || []).map((r) => ({
        value: r.value,
        label: r.display[localeKey] || r.display.en,
        areas: (r.areas || []).map((a) => ({
          value: a.value,
          label: a.display[localeKey] || a.display.en,
        })),
      }));

      if (!districts || districts.length === 0) return base;

      const provided = districts
        .map((d) => (typeof d === "string" ? d : d.value || ""))
        .filter(Boolean);

      const areaSet = new Set();
      base.forEach((r) => r.areas.forEach((a) => areaSet.add(a.value)));

      provided.forEach((dv) => {
        if (areaSet.has(dv)) return;
        for (const r of base) {
          if (dv.includes(r.value)) {
            r.areas.push({ value: dv, label: dv });
            break;
          }
        }
      });

      return base;
    } catch (e) {
      return [];
    }
  }, [localeKey, districts]);

  const flatAreas = useMemo(
    () => regionOptions.flatMap((r) => r.areas),
    [regionOptions]
  );

  const findRegionForArea = useCallback(
    (areaValue) => {
      if (!areaValue) return "";
      const r = regionOptions.find((reg) =>
        (reg.areas || []).some((a) => a.value === areaValue)
      );
      return r ? r.value : "";
    },
    [regionOptions]
  );

  const handleToggleDistrict = (areaValue) => {
    if (!areaValue) return;
    setSelectedDistricts((prev) => {
      const s = new Set(prev);
      if (s.has(areaValue)) s.delete(areaValue);
      else s.add(areaValue);
      return Array.from(s);
    });
  };

  const itemsList = useMemo(() => {
    const list = [];
    const q = (filterText || "").trim().toLowerCase();
    (regionOptions || []).forEach((r) => {
      const areas = (r.areas || []).filter(
        (a) => !q || (a.label || "").toLowerCase().includes(q)
      );
      if (areas.length === 0) return;
      list.push({ type: "header", regionValue: r.value, label: r.label });
      areas.forEach((a) =>
        list.push({
          type: "area",
          regionValue: r.value,
          areaValue: a.value,
          label: a.label,
        })
      );
    });
    return list;
  }, [regionOptions, filterText]);

  const computeRegionsFromSelection = useCallback(
    (selection) => {
      const regions = new Set();
      (selection || []).forEach((dv) => {
        const r = findRegionForArea(dv);
        if (r) regions.add(r);
      });
      const regionsArr = Array.from(regions);
      const counts = {};
      (selection || []).forEach((dv) => {
        const r = findRegionForArea(dv) || "";
        counts[r] = (counts[r] || 0) + 1;
      });
      let majority = "";
      let max = 0;
      Object.entries(counts).forEach(([k, v]) => {
        if (k && v > max) {
          max = v;
          majority = k;
        }
      });
      return { regions: regionsArr, majority: majority || "" };
    },
    [findRegionForArea]
  );

  // emit search whenever searchTerm or selectedDistricts change
  useEffect(() => {
    if (typeof onSearch === "function") {
      const meta = computeRegionsFromSelection(selectedDistricts || []);
      onSearch({
        searchTerm,
        districts: selectedDistricts,
        regions: meta.regions,
        majorityRegion: meta.majority,
      });
    }
  }, [searchTerm, selectedDistricts, onSearch, computeRegionsFromSelection]);

  useEffect(() => {
    const mq = window.matchMedia?.("(max-width: 640px)");
    const handler = () =>
      setIsMobile(!!mq?.matches || window.innerWidth <= 640);
    handler();
    try {
      mq?.addEventListener?.("change", handler);
    } catch (e) {
      // Safari fallback
      mq?.addListener?.(handler);
    }
    return () => {
      try {
        mq?.removeEventListener?.("change", handler);
      } catch (e) {
        mq?.removeListener?.(handler);
      }
    };
  }, []);

  // close on outside click
  useEffect(() => {
    function onDocClick(e) {
      if (!dropdownOpen) return;
      if (!comboRef.current) return;
      if (!comboRef.current.contains(e.target)) {
        closeDropdown();
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [dropdownOpen]);

  const openDropdown = () => {
    previousActiveElement.current = document.activeElement;
    setDropdownOpen(true);
    setTimeout(() => firstInputRef.current?.focus(), 0);
  };

  const closeDropdown = () => {
    setDropdownOpen(false);
    setFilterText("");
    try {
      previousActiveElement.current?.focus?.();
    } catch (e) {}
  };

  const resetSelection = () => {
    setSelectedDistricts([]);
    setFilterText("");
  };

  return (
    <div className="w-full" ref={comboRef}>
      <div className="relative">
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
          className="w-full"
        >
          <div className="flex bg-orange-600 items-center border border-gray-200 rounded overflow-hidden">
            <div className="px-3  ">
              <FaSearch />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={
                t("tutorCenters.searchPlaceholder") || "搜尋老師、科目、地區..."
              }
              className="flex-1 px-2 py-2 focus:outline-none"
            />

            <div className="flex items-center space-x-2 px-2 ">
              <button
                type="button"
                onClick={() =>
                  dropdownOpen ? closeDropdown() : openDropdown()
                }
                aria-haspopup="dialog"
                className=" text-white hover:text-gray-800 px-2"
              >
                {t("tutorCenters.selectArea") || "地區"}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Dialog / modal (responsive) */}
      {dropdownOpen && (
        <div
          className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black bg-opacity-40"
          role="dialog"
          aria-modal="true"
          onClick={closeDropdown}
        >
          <div
            className={`bg-white rounded-lg shadow-lg w-full sm:max-w-3xl mx-4 my-8 overflow-auto ${
              isMobile ? "h-full p-4" : "max-h-[80vh] p-4"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3 sticky top-0 bg-white z-10 px-2 py-2 shadow-sm">
              <button
                type="button"
                onClick={resetSelection}
                className="text-sm text-orange-600 hover:text-orange-800 font-medium"
              >
                {t("tutorCenters.reset") || "Reset"}
              </button>
              <button
                type="button"
                onClick={closeDropdown}
                aria-label="Close"
                className="text-gray-500 hover:text-gray-700 text-xl px-2"
              >
                {t("tutorCenters.ok") || "確定"}
              </button>
            </div>

            <div className="pb-2">
              <input
                ref={firstInputRef}
                type="text"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                placeholder={"選擇地區..."}
                className="w-full px-3 py-2 border border-gray-200 rounded focus:outline-none"
              />
            </div>

            <div>
              {itemsList.length === 0 && (
                <div className="text-sm text-gray-500">No areas</div>
              )}

              {/* grouped by region */}
              {(() => {
                const rows = [];
                let currentRegion = null;
                let currentAreas = [];

                itemsList.forEach((it) => {
                  if (it.type === "header") {
                    if (currentRegion)
                      rows.push({ region: currentRegion, areas: currentAreas });
                    currentRegion = it.label;
                    currentAreas = [];
                  } else {
                    currentAreas.push(it);
                  }
                });
                if (currentRegion)
                  rows.push({ region: currentRegion, areas: currentAreas });

                return rows.map((grp, gidx) => (
                  <div key={`grp-${gidx}`} className="mb-4">
                    <div
                      className={`font-medium ${
                        isMobile ? "text-base mb-2" : "text-sm"
                      } text-gray-700 px-1 py-0.5`}
                    >
                      {grp.region}
                    </div>

                    <div
                      className={`${
                        isMobile
                          ? "grid grid-cols-3 gap-1"
                          : "grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2"
                      } px-1 py-1`}
                    >
                      {grp.areas.map((it, idx) => (
                        <label
                          key={`a-${gidx}-${idx}`}
                          className={`${
                            isMobile
                              ? "flex items-center text-base w-full px-2 py-2 border rounded"
                              : "inline-flex items-center text-sm w-full px-2 py-1"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedDistricts.includes(it.areaValue)}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleToggleDistrict(it.areaValue);
                            }}
                            className={`${isMobile ? "mr-3" : "mr-2"}`}
                          />
                          <span className="truncate">{it.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Selected tags shown below the search input */}
      <div className="mt-2">
        <div className="flex flex-wrap gap-2">
          {selectedDistricts.map((dv) => {
            const area = flatAreas.find((a) => a.value === dv);
            return (
              <span
                key={dv}
                className="inline-flex items-center bg-gray-300 text-sm px-2 py-1 rounded-full"
              >
                <span className="mr-2 truncate max-w-[12rem]">
                  {area?.label || dv}
                </span>
                <button
                  type="button"
                  onClick={() => handleToggleDistrict(dv)}
                  className="text-xs px-1"
                  aria-label={`Remove ${dv}`}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>

        <div className="mt-2 text-sm text-gray-500">
          {t("tutorCenters.searchHint")}
        </div>
      </div>
    </div>
  );
}

export default TutorCenterSearch;
